// CloneQuestionPicker.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useFuncion } from "../../../context/FuncionContext";
import { useCourseStructure } from "../../../context/CourseStructureContext";

export default function CloneQuestionPicker({
  isOpen,
  onClose,
  onConfirm,
  forceSource, // compat
  lockLevelId,
  noImagesOnly, // ignorado
  excludeIds = [],
  examId,
  cursoId,
  cursoTipo,
}) {
  const {
    browseStructureQuestions,
    browseStructureMeta,
    // 🆕 bancos:
    getCourseBanks,
    getBankQuestions,
  } = useFuncion();

  const { toAbsoluteUrl } = useCourseStructure();

  // fuente dinámica según modo (estructura vs banco)
  const [fuenteActual, setFuenteActual] = useState("banco_preguntas");

  // ===== Estado de filtros comunes =====
  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState("");

  // Meta estructura
  const [niveles, setNiveles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [componentes, setComponentes] = useState([]);

  // 🆕 Bancos (para examen_solo)
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState("");

  // Selección de navegación (estructura)
  const [levelId, setLevelId] = useState(lockLevelId ? String(lockLevelId) : "");
  const [moduleId, setModuleId] = useState("");
  const [componentId, setComponentId] = useState("");

  // Paginación y listado
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // Selección y modo
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState("individual"); // 'individual' | 'pool'
  const [cantidad, setCantidad] = useState(10);

  // Preview (modal)
  const [previewQ, setPreviewQ] = useState(null);

  // Orden (sin dificultad/puntos)
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("asc");
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  // Responsive: cards en mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    const mql = window.matchMedia("(max-width: 860px)");
    const apply = () => setIsMobile(!!mql.matches);
    apply();
    if (mql.addEventListener) mql.addEventListener("change", apply);
    else mql.addListener(apply);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", apply);
      else mql.removeListener(apply);
    };
  }, [isOpen]);

  // Debounce búsqueda
  const [searchDraft, setSearchDraft] = useState("");
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => setSearch(searchDraft.trim()), 350);
    return () => clearTimeout(t);
  }, [searchDraft, isOpen]);

  // Bloqueo de nivel
  useEffect(() => {
    if (isOpen && lockLevelId) {
      setLevelId(String(lockLevelId));
      setModuleId("");
      setComponentId("");
      setPage(1);
    }
  }, [isOpen, lockLevelId]);

  useEffect(() => {
  if (!isOpen) return;
}, [isOpen, cursoId, lockLevelId, levelId]);

  // Cargar meta (niveles, módulos, componentes)
  useEffect(() => {
    if (!isOpen) return;

    const cid = Number(cursoId);
    if (!Number.isFinite(cid) || cid <= 0) {
      setErr("Falta cursoId para cargar niveles/módulos del curso.");
      setNiveles([]);
      setModulos([]);
      setComponentes([]);
      return;
    }

    (async () => {
      try {
        const meta = await browseStructureMeta(cid);
        setNiveles(meta?.niveles || []);
        setModulos(meta?.modulos || []);
        setComponentes(meta?.componentes || []);
      } catch (e) {
        setErr(e?.message || "No se pudo cargar la estructura del curso.");
      }
    })();
  }, [isOpen, browseStructureMeta, cursoId]);


  // examen_solo si la meta viene sin niveles
  const isExamSolo = cursoTipo === "examen_solo";
  const usesBankSource = fuenteActual === "banco_preguntas";

  // 🆕 ajustar fuente según tipo de curso
  useEffect(() => {
    setFuenteActual(isExamSolo ? "banco_preguntas" : "banco_preguntas");
  }, [isExamSolo]);

  // 🆕 Cargar bancos cuando se abra (si hay curso)
  useEffect(() => {
    if (!isOpen || !cursoId) return;
    (async () => {
      try {
        const list = await getCourseBanks(cursoId);
        setBanks(Array.isArray(list) ? list : []);
        if (!selectedBankId && Array.isArray(list) && list.length) {
          setSelectedBankId(String(list[0].id));
        }
      } catch {
        /* noop */
      }
    })();
  }, [isOpen, cursoId, getCourseBanks, selectedBankId]);

  // ===== Getters tolerantes para IDs =====
  const getNivelId = (o) => {
    if (!o) return "";
    const v =
      o.nivel_id ??
      o.nivelId ??
      o.level_id ??
      o.levelId ??
      o.curso_nivel_id ??
      o.cursoNivelId ??
      o.nivel?.id ??
      o.level?.id ??
      o.nivel?.nivel_id ??
      o.level?.level_id ??
      o.parent?.nivel_id ??
      o.parent?.level_id;

    return v == null ? "" : String(v);
  };

  const getModuloId = (o) => {
    if (!o) return "";
    const v =
      o.modulo_id ??
      o.moduloId ??
      o.parent_id ??
      o.parentId ??
      o.modulo?.id ??
      o.parent?.id;

    return v == null ? "" : String(v);
  };

  // ===== Derivados por selección =====
  const modulosFiltrados = useMemo(() => {
    if (!levelId) return modulos;

    const lid = String(levelId);
    const filtered = modulos.filter((m) => getNivelId(m) === lid);

    // ✅ fallback: si al filtrar por nivel quedas en 0 pero sí hay módulos,
    // probablemente el backend no está mandando nivel_id en el módulo.
    return filtered.length === 0 ? modulos : filtered;
  }, [modulos, levelId]);


  const componentesFiltrados = useMemo(() => {
    const lid = levelId ? String(levelId) : "";
    const mid = moduleId ? String(moduleId) : "";

    let filtered = componentes.filter((c) => {
      const nivOk = !lid || getNivelId(c) === lid;
      const modOk = !mid || getModuloId(c) === mid;
      return nivOk && modOk;
    });

    // ✅ fallback: si hay moduleId pero no filtró nada por IDs raros, intenta solo por modulo
    if (mid && filtered.length === 0) {
      filtered = componentes.filter((c) => getModuloId(c) === mid);
    }

    return filtered;
  }, [componentes, levelId, moduleId]);


  // ===== UX específicas por modo =====
  useEffect(() => {
    if (mode === "pool") {
      setSearchDraft("");
      setSearch("");
      setSelected(new Set());
      setPreviewQ(null);
    }
  }, [mode]);

  // Reset al cambiar nivel / módulo
  useEffect(() => {
    setModuleId("");
    setComponentId("");
    setPage(1);
  }, [levelId]);
  useEffect(() => {
    setComponentId("");
    setPage(1);
  }, [moduleId]);

  // Autoseleccionar componente si hay exactamente uno
  useEffect(() => {
    if (!!moduleId && componentesFiltrados.length === 1) {
      setComponentId(String(componentesFiltrados[0].id));
    }
  }, [moduleId, componentesFiltrados]);

  // ===== Helpers =====
  const ensureObj = (v) => {
    if (!v) return null;
    if (typeof v === "string") {
      try {
        return JSON.parse(v);
      } catch {
        return null;
      }
    }
    return typeof v === "object" ? v : null;
  };

  const exclSet = useMemo(() => new Set((excludeIds || []).map(Number)), [excludeIds]);

  const mapTipoLabel = (raw) => {
    const v = String(raw || "").trim();
    const dict = {
      seleccion_multiple: "Selección múltiple",
      verdadero_falso: "Verdadero/Falso",
      respuesta_corta: "Respuesta corta",
      completar: "Completar",
      ordenamiento: "Ordenamiento",
      emparejamiento: "Emparejamiento",
    };
    return dict[v] || (v ? toTitle(v.replaceAll("_", " ")) : "—");
  };

  const tipoOptions = useMemo(
    () => [
      { value: "", label: "Tipo (todos)" },
      { value: "seleccion_multiple", label: "Selección múltiple" },
      { value: "verdadero_falso", label: "Verdadero/Falso" },
      { value: "respuesta_corta", label: "Respuesta corta" },
      { value: "completar", label: "Completar" },
      { value: "ordenamiento", label: "Ordenamiento" },
      { value: "emparejamiento", label: "Emparejamiento" },
    ],
    []
  );

  // ===== Carga de resultados =====
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        if (usesBankSource) {
          // --- por Banco ---
          if (!selectedBankId) {
            setRows([]);
            setTotal(0);
            return;
          }
          const res = await getBankQuestions(cursoId, Number(selectedBankId), {
            page,
            limit,
            search,
            tipo,
          });
          let items = res?.items || res?.questions || res?.preguntas || res?.data || [];
          if (exclSet.size) items = items.filter((q) => !exclSet.has(Number(q.id)));
          setRows(items);
          setTotal(Number(res?.total || res?.count || items.length || 0));
        } else {
          // --- por Estructura ---
          const params = {
            page,
            limit,
            search,
            tipo,
            ...(lockLevelId ? { levelId: lockLevelId } : levelId ? { levelId } : {}),
            ...(moduleId ? { moduleId } : {}),
            ...(componentId ? { componentId } : {}),
            ...(cursoId !== undefined ? { cursoId } : {}),
          };
          const res = await browseStructureQuestions(params);
          let items = res?.items || res?.data || [];
          if (exclSet.size) items = items.filter((q) => !exclSet.has(Number(q.id)));
          setRows(items);
          setTotal(res?.total || res?.count || items.length || 0);
        }
      } catch (e) {
        setErr(e?.message || "No se pudo cargar");
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    page,
    limit,
    search,
    tipo,
    levelId,
    moduleId,
    componentId,
    browseStructureQuestions,
    lockLevelId,
    excludeIds,
    isExamSolo,
    usesBankSource,
    selectedBankId,
    getBankQuestions,
    cursoId,
  ]);

  // Reset al abrir
  useEffect(() => {
    if (!isOpen) return;

    setErr(null);
    setRows([]);
    setTotal(0);
    setPage(1);
    setSelected(new Set());
    setPreviewQ(null);

    setMode("individual");
    setSearchDraft("");
    setSearch("");
    setTipo("");
  }, [isOpen]);

  useEffect(() => {
    setSelected(new Set());
    setPage(1);
  }, [fuenteActual]);

  // si estamos en examen_solo y sort apunta a 'nivel' o 'modulo', reset
  useEffect(() => {
    if (usesBankSource && (sortBy === "nivel" || sortBy === "modulo")) {
      setSortBy("id");
      setSortDir("asc");
    }
  }, [usesBankSource, sortBy]);

  // ===== Ordenamiento cliente =====
  const sortedRows = useMemo(() => {
    const c = [...rows];
    const dir = sortDir === "asc" ? 1 : -1;
    c.sort((a, b) => {
      const pick = (r) => {
        switch (sortBy) {
          case "tipo":
            return String(r.tipo_pregunta || "");
          case "nivel":
            return r.nivel_orden ?? 0;
          case "modulo":
            return r.modulo_orden ?? 0;
          default:
            return Number(r.id ?? 0);
        }
      };
      const va = pick(a),
        vb = pick(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return c;
  }, [rows, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  // ===== Selección =====
  const masterRef = useRef(null);
  const idsOnPage = useMemo(() => sortedRows.map((r) => Number(r.id)), [sortedRows]);

  const allOnPageSelected =
    mode === "individual" && idsOnPage.length > 0 && idsOnPage.every((id) => selected.has(id));

  const someOnPageSelected = mode === "individual" && idsOnPage.some((id) => selected.has(id));

  useEffect(() => {
    if (masterRef.current) {
      masterRef.current.indeterminate = !allOnPageSelected && someOnPageSelected;
    }
  }, [allOnPageSelected, someOnPageSelected]);

  const toggleAllOnPage = () => {
    if (mode === "pool") return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) idsOnPage.forEach((id) => next.delete(id));
      else idsOnPage.forEach((id) => next.add(id));
      return next;
    });
  };

  const lastClickedIndex = useRef(null);
  const toggleOne = (id, rowIndex, shiftKey) => {
    if (mode === "pool") return;
    const nid = Number(id);
    const next = new Set(selected);
    if (shiftKey && lastClickedIndex.current != null) {
      const start = Math.min(lastClickedIndex.current, rowIndex);
      const end = Math.max(lastClickedIndex.current, rowIndex);
      for (let i = start; i <= end; i++) next.add(Number(sortedRows[i]?.id));
    } else {
      if (next.has(nid)) next.delete(nid);
      else next.add(nid);
      lastClickedIndex.current = rowIndex;
    }
    setSelected(next);
  };

  const clearSelection = () => setSelected(new Set());

  // ===== Preview helpers =====
  const parseOptionsForPreview = (q) => {
    const op = ensureObj(q?.opciones) || {};
    const arr = Array.isArray(op?.opciones)
      ? op.opciones
      : Array.isArray(op?.items)
        ? op.items
        : Array.isArray(q?.opciones)
          ? q.opciones
          : [];
    return arr.slice(0, 6).map((o, i) => ({
      texto: o?.texto ?? o?.label ?? String(o?.value ?? `Opción ${i + 1}`),
      media: o?.media || null,
    }));
  };

  // ===== Confirmar =====
  const handleConfirm = () => {
    if (mode === "pool") {
      if (usesBankSource) {
        if (!selectedBankId) return alert("Selecciona un banco para crear el pool.");
        if (!cantidad || Number(cantidad) <= 0) return alert("Indica una cantidad válida (> 0).");
        const criterios = {};
        if (tipo) criterios.tipo = [tipo];

        onConfirm({
          mode: "pool",
          fuente: "banco_preguntas",
          bank_id: Number(selectedBankId),
          scope: "banco",
          include_subtree: false,
          cantidad: Number(cantidad),
          criterios,
          examId,
          cursoId,
        });
        clearSelection();
        return;
      }

      // Estructura (como lo tenías)
      if (!moduleId) return alert("Selecciona un módulo para crear el pool (puedes refinar por componente después).");
      if (!cantidad || Number(cantidad) <= 0) return alert("Indica una cantidad válida (> 0).");

      const criterios = {};
      if (tipo) criterios.tipo = [tipo];

      const scopeType = componentId ? "componente" : "modulo";
      const scopeId = componentId || moduleId;

      onConfirm({
        mode: "pool",
        fuente: "estructura_preguntas",
        estructura_id: Number(scopeId),
        scope: scopeType,
        include_subtree: true,
        cantidad: Number(cantidad),
        criterios,
        examId,
      });
      clearSelection();
      return;
    }

    // Individual
    const ids = [...selected];
    if (!ids.length) return onClose();

    if (usesBankSource) {
      onConfirm({
        mode: "individual",
        fuente: "banco_preguntas",
        ids,
        bank_id: selectedBankId ? Number(selectedBankId) : undefined,
        examId,
        cursoId,
      });
    } else {
      onConfirm({ mode: "individual", fuente: "estructura_preguntas", ids, examId });
    }
    clearSelection();
  };

  // ===== UI =====
  if (!isOpen) return null;

  const contextLabel = (() => {
    if (usesBankSource) {
      if (selectedBankId) return "Mostrando preguntas del banco seleccionado";
      return "Selecciona un banco para ver preguntas";
    }
    if (componentId) return "Mostrando: componente (incluye descendencia)";
    if (moduleId) return "Mostrando: módulo";
    if (levelId) return "Mostrando: nivel";
    return "Mostrando: todas las preguntas";
  })();

  // ✅ Requisitos mínimos según modo
  const hasRequiredContext = usesBankSource
    ? !!cursoId && !!selectedBankId // banco necesita curso + banco
    : true;                         // estructura NO depende de cursoId para permitir importar

  const hasScopeForPool = usesBankSource ? !!selectedBankId : !!moduleId;

  const canConfirm =
    !loading &&
    hasRequiredContext &&
    ((mode === "individual" && selected.size > 0) ||
      (mode === "pool" && Number(cantidad) > 0 && hasScopeForPool));



  return (
    <div style={S.overlay} role="dialog" aria-modal="true">
      <style>{CSS}</style>

      <div style={S.modal}>
        {/* Header */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <div style={S.title}>Seleccionar preguntas</div>
            <div style={S.subtitle}>
              {loading ? "Cargando…" : `${total} resultado${total === 1 ? "" : "s"}`} • {contextLabel}
              {err ? <span style={{ color: "var(--danger)" }}> • {err}</span> : null}
            </div>
          </div>

          <div style={S.headerRight}>
            <span style={S.badge}>Curso ID: {cursoId ?? "—"}</span>
            <button
              onClick={() => {
                clearSelection();
                setPreviewQ(null);
                onClose();
              }}
              style={S.iconBtn}
              aria-label="Cerrar"
              title="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={S.tabsWrap}>
          <div style={S.tabs}>
            <button
              onClick={() => setMode("individual")}
              style={{ ...S.tab, ...(mode === "individual" ? S.tabActive : {}) }}
            >
              Individual
              {mode === "individual" ? <span style={S.pill}>{selected.size}</span> : null}
            </button>
            <button onClick={() => setMode("pool")} style={{ ...S.tab, ...(mode === "pool" ? S.tabActive : {}) }}>
              Pregunta aleatoria
            </button>
          </div>
        </div>

        {/* Controls */}
        <div style={S.controls}>
          {/* Buscar (solo individual) */}
          {mode !== "pool" && (
            <div style={S.field}>
              <label style={S.label}>Buscar</label>
              <input
                placeholder="Escribe para buscar…"
                value={searchDraft}
                onChange={(e) => {
                  setSearchDraft(e.target.value);
                  setPage(1);
                }}
                style={S.input}
              />
            </div>
          )}

          {/* Tipo */}
          <div style={S.field}>
            <label style={S.label}>Tipo</label>
            <select
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value);
                setPage(1);
              }}
              style={S.input}
            >
              {tipoOptions.map((o) => (
                <option key={o.value || "all"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Estructura / Banco */}
          {!isExamSolo && (
            <div style={S.field}>
              <label style={S.label}>Fuente</label>
              <select
                value={fuenteActual}
                onChange={(e) => {
                  setFuenteActual(e.target.value);
                  setPage(1);
                }}
                style={S.input}
              >
                <option value="banco_preguntas">Banco de preguntas</option>
                <option value="estructura_preguntas">Preguntas del curso</option>
              </select>
            </div>
          )}
          {usesBankSource ? (
            <div style={S.field}>
              <label style={S.label}>{mode === "pool" ? "Ámbito (Banco)" : "Banco"}</label>
              <select
                value={selectedBankId}
                onChange={(e) => {
                  setSelectedBankId(e.target.value);
                  setPage(1);
                }}
                style={S.input}
                title="Banco de preguntas"
              >
                {banks.length === 0 ? (
                  <option value="" disabled>
                    — Sin bancos —
                  </option>
                ) : (
                  banks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.nombre} ({b.preguntas_count ?? 0})
                    </option>
                  ))
                )}
              </select>
            </div>
          ) : (
            <>
              <div style={S.field}>
                <label style={S.label}>Nivel</label>
                <select
                  value={levelId}
                  onChange={(e) => setLevelId(e.target.value)}
                  style={S.input}
                  disabled={!!lockLevelId}
                  title="Nivel"
                >
                  {lockLevelId ? (
                    <option value={lockLevelId}>Nivel {lockLevelId}</option>
                  ) : (
                    <>
                      <option value="">Todos</option>
                      {niveles.map((n) => (
                        <option key={n.id} value={n.id}>
                          {n.nombre || `Nivel ${n.orden ?? n.id}`}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {/* En Pool ocultamos la navegación de estructura dentro del grid y la mostramos en barra */}
              {mode !== "pool" && (
                <>
                  <div style={S.field}>
                    <label style={S.label}>Módulo</label>
                    <select
                      value={moduleId}
                      onChange={(e) => {
                        setModuleId(e.target.value);
                        setPage(1);
                      }}
                      style={S.input}
                      title="Módulo"
                    >
                      <option value="">Todos</option>
                      {modulosFiltrados.length === 0 ? (
                        <option value="" disabled>
                          — No hay módulos —
                        </option>
                      ) : (
                        modulosFiltrados.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.nombre || `Módulo ${m.orden ?? m.id}`}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div style={S.field}>
                    <label style={S.label}>Componente</label>
                    <select
                      value={componentId}
                      onChange={(e) => {
                        setComponentId(e.target.value);
                        setPage(1);
                      }}
                      style={S.input}
                      title="Componente"
                      disabled={!moduleId}
                    >
                      <option value="">{moduleId ? "Todos" : "Elige módulo"}</option>
                      {componentesFiltrados.length === 0
                        ? moduleId
                          ? [
                            <option key="none" value="" disabled>
                              — Este módulo no tiene componentes —
                            </option>,
                          ]
                          : null
                        : componentesFiltrados.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nombre || `Comp. ${c.orden ?? c.id}`}
                          </option>
                        ))}
                    </select>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Pool bar */}
        {mode === "pool" && (
          <div style={S.poolBar}>
            {!usesBankSource && (
              <>
                <div style={S.poolGroup}>
                  <div style={S.poolHint}>Ámbito</div>
                  <div style={S.poolRow}>
                    <select
                      value={moduleId}
                      onChange={(e) => {
                        setModuleId(e.target.value);
                        setComponentId("");
                      }}
                      style={{ ...S.input, minWidth: 240 }}
                      title="Módulo"
                    >
                      <option value="">Módulo (elige uno)</option>
                      {modulosFiltrados.length === 0 ? (
                        <option value="" disabled>
                          — No hay módulos —
                        </option>
                      ) : (
                        modulosFiltrados.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.nombre || `Módulo ${m.orden ?? m.id}`}
                          </option>
                        ))
                      )}
                    </select>

                    {!!moduleId && (
                      <select
                        value={componentId}
                        onChange={(e) => setComponentId(e.target.value)}
                        style={{ ...S.input, minWidth: 240 }}
                        title="Refinar por componente (opcional)"
                      >
                        <option value="">Todos los componentes</option>
                        {componentesFiltrados.length === 0 ? (
                          <option value="" disabled>
                            — Este módulo no tiene componentes —
                          </option>
                        ) : (
                          componentesFiltrados.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.nombre || `Comp. ${c.orden ?? c.id}`}
                            </option>
                          ))
                        )}
                      </select>
                    )}
                  </div>
                </div>
              </>
            )}

            <div style={S.poolGroup}>
              <div style={S.poolHint}>Cantidad</div>
              <div style={S.poolRow}>
                <input
                  type="number"
                  min={1}
                  value={cantidad}
                  onChange={(e) => setCantidad(e.target.value)}
                  style={{ ...S.input, width: 140 }}
                  placeholder="Cantidad"
                />
                <span style={S.poolNote}>
                  {usesBankSource ? "Fuente: Banco" : "Incluye descendencia: Sí"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div style={S.body}>
          {/* Top pagination */}
          <div style={S.pagination}>
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={S.pageBtn}
            >
              ←
            </button>
            <div style={S.pageInfo}>
              Página <b>{page}</b> / {totalPages}
            </div>
            <button
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              style={S.pageBtn}
            >
              →
            </button>
          </div>

          {/* Results */}
          {loading ? (
            <div style={S.skeletonWrap}>
              {[...Array(6)].map((_, i) => (
                <div key={i} style={S.skeletonCard} />
              ))}
            </div>
          ) : !sortedRows.length ? (
            <div style={S.empty}>
              {usesBankSource && !selectedBankId ? "Selecciona un banco" : "Sin resultados"}
            </div>
          ) : isMobile ? (
            <div style={S.cards}>
              {sortedRows.map((r, idx) => {
                const id = Number(r.id);
                const checked = selected.has(id);
                const disabled = mode === "pool";
                return (
                  <div key={id} style={S.card}>
                    <div style={S.cardTop}>
                      <label style={S.checkWrap} title={disabled ? "En pool no se selecciona individual" : ""}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={(e) => toggleOne(id, idx, e?.shiftKey)}
                        />
                        <span style={S.checkText}>Seleccionar</span>
                      </label>

                      <div style={S.cardBadges}>
                        <span style={S.tipoPill}>{mapTipoLabel(r.tipo_pregunta)}</span>
                        {!usesBankSource && (
                          <span style={S.ghostPill}>
                            Nivel {r.nivel_orden ?? "—"} • Módulo {r.modulo_orden ?? "—"}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={S.cardQ} title={String(r.pregunta || "")}>
                      {truncateText(r.pregunta, 50)}
                    </div>

                    {r.explicacion ? (
                      <div style={S.cardHint} title={String(r.explicacion || "")}>
                        {truncateText(r.explicacion, 50)}
                      </div>
                    ) : null}

                    <div style={S.cardActions}>
                      <button
                        onClick={() => setPreviewQ(r)}
                        style={S.ghostBtn}
                        title="Vista previa"
                      >
                        Vista previa
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, width: 54, textAlign: "center" }}>
                      <input
                        ref={masterRef}
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleAllOnPage}
                        disabled={mode === "pool" || loading || idsOnPage.length === 0}
                        title={allOnPageSelected ? "Deseleccionar página" : "Seleccionar página"}
                      />
                    </th>

                    <Th onClick={() => toggleSort("id")} style={S.th}>
                      Pregunta {sortIcon(sortBy, sortDir, "id")}
                    </Th>

                    <Th onClick={() => toggleSort("tipo")} style={{ ...S.th, width: 200 }}>
                      Tipo {sortIcon(sortBy, sortDir, "tipo")}
                    </Th>

                    {!usesBankSource && (
                      <>
                        <Th onClick={() => toggleSort("nivel")} style={{ ...S.th, width: 110 }}>
                          Nivel {sortIcon(sortBy, sortDir, "nivel")}
                        </Th>
                        <Th onClick={() => toggleSort("modulo")} style={{ ...S.th, width: 120 }}>
                          Módulo {sortIcon(sortBy, sortDir, "modulo")}
                        </Th>
                      </>
                    )}

                    <th style={{ ...S.th, width: 160, textAlign: "right" }}>Acciones</th>
                  </tr>
                </thead>

                <tbody>
                  {sortedRows.map((r, idx) => {
                    const id = Number(r.id);
                    const checked = selected.has(id);
                    const disabled = mode === "pool";

                    return (
                      <tr key={id} style={S.tr}>
                        <td style={{ ...S.td, textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => toggleOne(id, idx, e?.shiftKey)}
                            disabled={disabled}
                          />
                        </td>

                        <td style={S.td}>
                          <div style={S.qLineClamp} title={String(r.pregunta || "")}>
                            {truncateText(r.pregunta, 50)}
                          </div>
                          {r.explicacion ? (
                            <div style={S.hintLineClamp} title={String(r.explicacion || "")}>
                              {truncateText(r.explicacion, 50)}
                            </div>
                          ) : null}
                        </td>

                        <td style={S.td}>
                          <span style={S.tipoBadge}>{mapTipoLabel(r.tipo_pregunta)}</span>
                        </td>

                        {!usesBankSource && (
                          <>
                            <td style={{ ...S.td, textAlign: "center" }}>{r.nivel_orden ?? "—"}</td>
                            <td style={{ ...S.td, textAlign: "center" }}>{r.modulo_orden ?? "—"}</td>
                          </>
                        )}

                        <td style={{ ...S.td, textAlign: "right" }}>
                          <button onClick={() => setPreviewQ(r)} style={S.ghostBtn}>
                            Vista previa
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <div style={S.footerLeft}>
            {mode === "individual" ? (
              <div style={S.selInfo}>
                <span>
                  Seleccionadas: <b>{selected.size}</b>
                </span>
                <button onClick={clearSelection} style={S.clearBtn} disabled={selected.size === 0}>
                  Limpiar
                </button>
              </div>
            ) : (
              <div style={S.poolInfo}>
                Pool: se importarán <b>{cantidad}</b> aleatorias {tipo ? <>• tipo: <b>{mapTipoLabel(tipo)}</b></> : null}
              </div>
            )}
          </div>

          <div style={S.footerRight}>
            <button
              onClick={() => {
                clearSelection();
                setPreviewQ(null);
                onClose();
              }}
              style={S.cancelBtn}
            >
              Cancelar
            </button>

            <button
              onClick={() => {
                if (mode === "pool" && !hasScopeForPool) {
                  return alert(usesBankSource ? "Selecciona un banco para el pool." : "Selecciona un módulo para el pool.");
                }
                handleConfirm();
              }}
              style={{ ...S.primaryBtn, ...(canConfirm ? null : S.primaryBtnDisabled) }}
              disabled={!canConfirm}
              title={
                usesBankSource && !cursoId
                  ? "Falta cursoId (examen_solo)"
                  : usesBankSource && !selectedBankId
                    ? "Selecciona un banco"
                    : mode === "pool" && !hasScopeForPool
                      ? (usesBankSource ? "Selecciona un banco para el pool" : "Selecciona un módulo para el pool")
                      : ""
              }
            >
              {mode === "pool" ? "Importar pool" : "Importar seleccionadas"}
            </button>
          </div>
        </div>

        {/* Preview Modal */}
        {previewQ ? (
          <PreviewModal
            q={previewQ}
            onClose={() => setPreviewQ(null)}
            toAbsoluteUrl={toAbsoluteUrl}
            parseOptionsForPreview={parseOptionsForPreview}
            mapTipoLabel={mapTipoLabel}
            isExamSolo={usesBankSource}
          />
        ) : null}
      </div>
    </div>
  );
}

/* ========================= Preview Modal ========================= */
function PreviewModal({ q, onClose, toAbsoluteUrl, parseOptionsForPreview, mapTipoLabel, isExamSolo }) {
  const optsPrev = parseOptionsForPreview(q);
  const op = typeof q?.opciones === "string" ? safeJSON(q.opciones) || {} : q?.opciones || {};
  const media = q?.media || op?.media || null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={PM.overlay} role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div style={PM.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={PM.header}>
          <div>
            <div style={PM.title}>Vista previa</div>
            <div style={PM.subtitle}>
              <span style={PM.badge}>{mapTipoLabel(q?.tipo_pregunta)}</span>
              {!isExamSolo ? (
                <span style={{ color: "var(--muted)" }}>
                  • Nivel {q?.nivel_orden ?? "—"} • Módulo {q?.modulo_orden ?? "—"}
                </span>
              ) : null}
            </div>
          </div>
          <button style={PM.closeBtn} onClick={onClose} aria-label="Cerrar vista previa">
            ✕
          </button>
        </div>

        <div style={PM.body}>
          <div style={PM.section}>
            <div style={PM.sectionTitle}>Enunciado</div>
            <div style={PM.box}>{truncateText(q?.pregunta, 40) || "—"}</div>

            {optsPrev?.length ? (
              <>
                <div style={{ ...PM.sectionTitle, marginTop: 14 }}>Opciones</div>
                <div style={PM.opts}>
                  {optsPrev.map((o, i) => (
                    <div key={i} style={PM.optItem}>
                      <div style={PM.optText}>
                        <span style={PM.optIndex}>{i + 1}</span>
                        <span>{o.texto}</span>
                      </div>
                      {o.media ? (
                        <div style={{ marginTop: 8 }}>
                          <RenderMedia media={o.media} toAbsoluteUrl={toAbsoluteUrl} compact />
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {q?.explicacion ? (
              <>
                <div style={{ ...PM.sectionTitle, marginTop: 14 }}>Explicación</div>
                <div style={PM.boxMuted}>{q.explicacion}</div>
              </>
            ) : null}
          </div>

          <div style={PM.section}>
            <div style={PM.sectionTitle}>Multimedia</div>
            <div style={PM.mediaBox}>
              {!media ? (
                <span style={{ color: "var(--muted)" }}>— sin multimedia —</span>
              ) : (
                <RenderMedia media={media} toAbsoluteUrl={toAbsoluteUrl} />
              )}
            </div>
          </div>
        </div>

        <div style={PM.footer}>
          <button style={PM.secondaryBtn} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function RenderMedia({ media, toAbsoluteUrl, compact }) {
  if (!media?.type || !media?.url) return null;
  const src = toAbsoluteUrl(media.url);
  if (!src) return null;

  if (media.type === "audio") {
    return (
      <figure style={{ margin: compact ? "4px 0" : "6px 0 12px" }}>
        <audio controls src={src} style={{ width: "100%" }} />
        {media.caption ? <figcaption style={S.figcap}>{media.caption}</figcaption> : null}
      </figure>
    );
  }
  return (
    <figure style={{ margin: compact ? "4px 0" : "6px 0 12px" }}>
      <img
        src={src}
        alt={media.alt || "Imagen"}
        style={{
          maxWidth: compact ? 240 : "100%",
          borderRadius: 12,
          border: "1px solid var(--border)",
          display: "block",
        }}
      />
      {media.caption ? <figcaption style={S.figcap}>{media.caption}</figcaption> : null}
    </figure>
  );
}

/* ========================= Helpers UI ========================= */
function Th({ children, onClick, style }) {
  return (
    <th style={{ ...style, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      {children}
    </th>
  );
}

function sortIcon(activeBy, dir, me) {
  if (activeBy !== me) return null;
  return <span style={{ marginLeft: 6, opacity: 0.6 }}>{dir === "asc" ? "▲" : "▼"}</span>;
}

const safeJSON = (v) => {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

function toTitle(s) {
  const x = String(s || "").trim();
  if (!x) return "";
  return x.charAt(0).toUpperCase() + x.slice(1);
}

function truncateText(input, maxChars = 120) {
  const s = String(input ?? "").replace(/\s+/g, " ").trim();
  if (!s) return "—";
  if (s.length <= maxChars) return s;

  // evita cortar a la mitad una palabra (si se puede)
  const cut = s.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(" ");
  const nice = lastSpace > Math.floor(maxChars * 0.6) ? cut.slice(0, lastSpace) : cut;

  return nice.replace(/[.,;:!?]+$/g, "").trim() + "…";
}


/* ========================= Styles ========================= */
const CSS = `
:root{
  --bg: #0b1220;
  --panel: #0f172a;
  --card: #0b1220;
  --paper: #ffffff;
  --text: #0f172a;
  --muted: #6b7280;
  --muted2: #94a3b8;
  --border: #e5e7eb;
  --border2: rgba(148,163,184,.25);
  --shadow: 0 22px 70px rgba(0,0,0,.35);
  --shadow2: 0 18px 40px rgba(2,6,23,.20);
  --primary1: #2563eb;
  --primary2: #1d4ed8;
  --danger: #ef4444;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
`;

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,.68)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1100,
    padding: 14,
  },
  modal: {
    width: "min(1120px, 96vw)",
    maxHeight: "92vh",
    background: "var(--paper)",
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "var(--shadow)",
    display: "flex",
    flexDirection: "column",
    border: "1px solid rgba(226,232,240,.7)",
  },

  header: {
    padding: "14px 16px",
    borderBottom: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  headerLeft: { minWidth: 0, flex: 1 },
  title: { fontSize: 16, fontWeight: 900, color: "#0f172a", letterSpacing: 0.2 },
  subtitle: { marginTop: 2, fontSize: 12.5, color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  headerRight: { display: "flex", alignItems: "center", gap: 10 },
  badge: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(37,99,235,.08)",
    border: "1px solid rgba(37,99,235,.25)",
    color: "#1d4ed8",
    fontWeight: 800,
    fontSize: 12,
  },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "#fff",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
  },

  tabsWrap: { padding: "10px 16px 0" },
  tabs: {
    display: "inline-flex",
    gap: 6,
    padding: 6,
    borderRadius: 999,
    background: "rgba(15,23,42,.06)",
    border: "1px solid rgba(15,23,42,.08)",
  },
  tab: {
    padding: "9px 12px",
    borderRadius: 999,
    border: "1px solid transparent",
    background: "transparent",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  tabActive: {
    background: "#fff",
    border: "1px solid rgba(15,23,42,.10)",
    boxShadow: "0 1px 0 rgba(2,6,23,.05)",
    color: "#0f172a",
  },
  pill: {
    padding: "2px 8px",
    borderRadius: 999,
    background: "rgba(37,99,235,.10)",
    border: "1px solid rgba(37,99,235,.18)",
    color: "#1d4ed8",
    fontWeight: 900,
    fontSize: 12,
  },

  controls: {
    padding: "12px 16px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 10,
    alignItems: "end",
    borderBottom: "1px solid var(--border)",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 12, fontWeight: 800, color: "#475569" },
  input: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "#fff",
    fontSize: 14,
    outline: "none",
  },

  poolBar: {
    padding: "10px 16px 12px",
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
    borderBottom: "1px solid var(--border)",
    background: "linear-gradient(180deg, rgba(15,23,42,.03), rgba(15,23,42,0))",
  },
  poolGroup: { display: "flex", flexDirection: "column", gap: 8 },
  poolHint: { fontSize: 12, fontWeight: 900, color: "#475569" },
  poolRow: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  poolNote: { fontSize: 12.5, color: "var(--muted)", fontWeight: 700 },

  body: { padding: 16, overflow: "auto" },

  pagination: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
  },
  pageBtn: {
    padding: "8px 10px",
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    minWidth: 44,
  },
  pageInfo: { fontSize: 13, color: "var(--muted)" },

  table: {
    width: "100%",
    minWidth: 860,
    borderCollapse: "separate",
    borderSpacing: 0,
    border: "1px solid var(--border)",
    borderRadius: 16,
    overflow: "hidden",
    background: "#fff",
  },
  th: {
    padding: 12,
    textAlign: "left",
    fontWeight: 900,
    fontSize: 12.5,
    color: "#334155",
    background: "rgba(15,23,42,.03)",
    borderBottom: "1px solid var(--border)",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  tr: { borderBottom: "1px solid var(--border)" },
  td: {
    padding: 12,
    verticalAlign: "top",
    borderBottom: "1px solid rgba(226,232,240,.85)",
  },

  qLineClamp: {
    fontWeight: 900,
    color: "#0f172a",
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,          // 👈 1 línea (pon 2 si quieres un poco más)
    overflow: "hidden",
    textOverflow: "ellipsis",

    // ✅ fallback (si no aplica line-clamp)
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },

  hintLineClamp: {
    marginTop: 6,
    color: "var(--muted)",
    fontSize: 12.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.25,
  },

  tipoBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(37,99,235,.08)",
    border: "1px solid rgba(37,99,235,.18)",
    color: "#1d4ed8",
    fontWeight: 900,
    fontSize: 12.5,
    whiteSpace: "nowrap",
  },

  ghostBtn: {
    padding: "8px 10px",
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
  },

  // Mobile cards
  cards: { display: "grid", gap: 12 },
  card: {
    border: "1px solid var(--border)",
    borderRadius: 16,
    background: "#fff",
    boxShadow: "0 10px 26px rgba(2,6,23,.06)",
    padding: 12,
  },
  cardTop: { display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" },
  checkWrap: { display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 900, color: "#334155" },
  checkText: { fontSize: 12.5, color: "#334155" },
  cardBadges: { display: "inline-flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" },
  tipoPill: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(37,99,235,.10)",
    border: "1px solid rgba(37,99,235,.18)",
    color: "#1d4ed8",
    fontWeight: 900,
    fontSize: 12,
  },
  ghostPill: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(15,23,42,.05)",
    border: "1px solid rgba(15,23,42,.08)",
    color: "#334155",
    fontWeight: 900,
    fontSize: 12,
  },
  cardQ: {
    marginTop: 10,
    fontWeight: 950,
    color: "#0f172a",
    lineHeight: 1.25,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,     // 👈 2 líneas en mobile
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  cardHint: {
    marginTop: 8,
    color: "var(--muted)",
    fontSize: 12.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardActions: { marginTop: 12, display: "flex", justifyContent: "flex-end" },

  // Empty & loading
  empty: {
    padding: 18,
    textAlign: "center",
    color: "var(--muted)",
    border: "1px dashed rgba(148,163,184,.45)",
    borderRadius: 16,
    background: "rgba(15,23,42,.02)",
  },
  skeletonWrap: { display: "grid", gap: 12 },
  skeletonCard: {
    height: 96,
    borderRadius: 16,
    border: "1px solid rgba(226,232,240,.9)",
    background: "linear-gradient(90deg, rgba(15,23,42,.05), rgba(15,23,42,.09), rgba(15,23,42,.05))",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.2s infinite",
  },

  footer: {
    padding: 14,
    borderTop: "1px solid var(--border)",
    display: "flex",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    background: "linear-gradient(180deg, rgba(15,23,42,.01), rgba(15,23,42,.03))",
  },
  footerLeft: { minWidth: 240 },
  footerRight: { display: "flex", gap: 10, alignItems: "center" },

  selInfo: {
    display: "inline-flex",
    gap: 10,
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 14,
    background: "rgba(37,99,235,.08)",
    border: "1px solid rgba(37,99,235,.18)",
    color: "#1d4ed8",
    fontWeight: 900,
  },
  clearBtn: {
    padding: "7px 10px",
    borderRadius: 12,
    border: "1px solid rgba(37,99,235,.22)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
  },
  poolInfo: { color: "var(--muted)", fontSize: 13, fontWeight: 800 },

  cancelBtn: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid var(--border)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
  },
  primaryBtn: {
    padding: "10px 14px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, var(--primary1), var(--primary2))",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
    boxShadow: "var(--shadow2)",
  },
  primaryBtnDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
    boxShadow: "none",
  },

  figcap: { fontSize: 12, color: "var(--muted)", marginTop: 6 },
};

const PM = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,.72)",
    zIndex: 1200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 14,
  },
  modal: {
    width: "min(980px, 96vw)",
    maxHeight: "92vh",
    background: "#fff",
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid rgba(226,232,240,.8)",
    boxShadow: "var(--shadow)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: "14px 16px",
    borderBottom: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  title: { fontSize: 15, fontWeight: 950, color: "#0f172a" },
  subtitle: { marginTop: 4, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  badge: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(37,99,235,.10)",
    border: "1px solid rgba(37,99,235,.18)",
    color: "#1d4ed8",
    fontWeight: 950,
    fontSize: 12,
  },
  closeBtn: {
    width: 38,
    height: 38,
    borderRadius: 14,
    border: "1px solid var(--border)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
  },
  body: {
    padding: 16,
    overflow: "auto",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 14,
    alignItems: "start",
  },
  section: { display: "flex", flexDirection: "column", gap: 10 },
  sectionTitle: { fontSize: 12, fontWeight: 950, color: "#475569", letterSpacing: 0.2 },
  box: {
    padding: 12,
    borderRadius: 16,
    border: "1px solid var(--border)",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 800,
    lineHeight: 1.35,
  },
  boxMuted: {
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(226,232,240,.9)",
    background: "rgba(15,23,42,.02)",
    color: "#334155",
    lineHeight: 1.35,
    fontWeight: 700,
  },
  opts: { display: "grid", gap: 10 },
  optItem: {
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(226,232,240,.9)",
    background: "rgba(15,23,42,.02)",
  },
  optText: { display: "flex", gap: 10, alignItems: "flex-start", fontWeight: 800, color: "#0f172a" },
  optIndex: {
    width: 24,
    height: 24,
    borderRadius: 999,
    background: "rgba(37,99,235,.12)",
    border: "1px solid rgba(37,99,235,.18)",
    color: "#1d4ed8",
    fontWeight: 950,
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
    marginTop: 1,
  },
  mediaBox: {
    padding: 12,
    borderRadius: 16,
    border: "1px dashed rgba(148,163,184,.5)",
    background: "rgba(15,23,42,.02)",
  },
  footer: {
    padding: 14,
    borderTop: "1px solid var(--border)",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    background: "rgba(15,23,42,.02)",
  },
  secondaryBtn: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid var(--border)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
  },
};
